import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Componentes
import LandingPage from './components/LandingPage';
import LegalPage from './components/LegalPage';
import CustomerApp from './components/CustomerApp';
import PublicMesaOrder from './components/PublicMesaOrder';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Layout from './components/Layout';
import Lanchonetes from './components/Lanchonetes';
import AdminLanchonete from './components/AdminLanchonete';
import Produtos from './components/Produtos';
import Estoque from './components/Estoque';
import Categorias from './components/Categorias';
import Pedidos from './components/Pedidos';
import FechamentoCaixa from './components/FechamentoCaixa';
import Usuarios from './components/Usuarios';
import Motoboys from './components/Motoboys';
import MotoboysManagement from './components/MotoboysManagement';
import MotoboyDashboard from './components/MotoboyDashboard';
import BannersSection from './components/BannersSection';
import MotoboyDeliveries from './components/MotoboyDeliveries';
import ProductOptionsManager from './components/ProductOptionsManager';
import MesasManagement from './components/MesasManagement';
import ProtectedRoute from './components/ProtectedRoute';
import Subscriptions from './components/Subscriptions';
import Promocoes from './components/Promocoes';
import PrintStation from './components/PrintStation';
import InternalChat from './components/InternalChat';
import StripeTestLab from './components/StripeTestLab';
import StoreSignup from './components/StoreSignup';
import StoreOnboarding from './components/StoreOnboarding';
import CustomerFeatures from './components/CustomerFeatures';
import AndroidAppUpdate from './components/AndroidAppUpdate';
import UpdateAnnouncement from './components/UpdateAnnouncement';
import DeliveryPricing from './components/DeliveryPricing';
import WhatsAppTestLab from './components/WhatsAppTestLab';
import WhatsAppMonitoring from './components/WhatsAppMonitoring';
import OperationsCenter from './components/OperationsCenter';
import KitchenQueue from './components/KitchenQueue';

// Serviços
import { AuthProvider } from './services/AuthService';

// Tema
const theme = createTheme({
  palette: {
    primary: {
      main: '#E50914',
      light: '#FF3B45',
      dark: '#B20710',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#B20710',
      light: '#E50914',
      dark: '#7F0007',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F4F4F5',
      paper: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#E50914',
          backgroundImage: 'none',
          boxShadow: '0 4px 16px rgba(127, 0, 7, 0.18)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 10, textTransform: 'none', fontWeight: 700 },
        containedPrimary: { boxShadow: '0 4px 12px rgba(229, 9, 20, 0.22)' },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: '#FFF1F2',
            color: '#B20710',
          },
          '&.Mui-selected .MuiListItemIcon-root': { color: '#E50914' },
          '&.Mui-selected:hover': { backgroundColor: '#FFE4E6' },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: '#E50914', height: 3 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { '&.Mui-selected': { color: '#E50914' } },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': { color: '#E50914' },
          '&.Mui-checked + .MuiSwitch-track': { backgroundColor: '#E50914' },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { borderBottom: '1px solid #FECACA' },
      },
    },
  },
});

function AdminMotoboyRoutes({ prefix = '' }) {
  return (
    <AuthProvider>
      <ProtectedRoute>
        <Routes>
          <Route path="/" element={<Navigate to={`${prefix}/motoboy/dashboard`} replace />} />
          <Route path="dashboard" element={<MotoboyDashboard />} />
        </Routes>
      </ProtectedRoute>
    </AuthProvider>
  );
}

function AdminRoutes({ prefix = '' }) {
  return (
    <AuthProvider>
      <ProtectedRoute>
        <Layout>
          <Routes>
            <Route path="/" element={<Navigate to={`${prefix}/dashboard`} replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="lanchonetes" element={<Lanchonetes />} />
            <Route path="admin-lanchonete" element={<AdminLanchonete />} />
            <Route path="produtos" element={<Produtos />} />
            <Route path="estoque" element={<Estoque />} />
            <Route path="categorias" element={<Categorias />} />
            <Route path="pedidos" element={<Pedidos />} />
            <Route path="cozinha" element={<KitchenQueue />} />
            <Route path="fechamento" element={<FechamentoCaixa />} />
            <Route path="usuarios" element={<Usuarios />} />
            <Route path="motoboys" element={<Motoboys />} />
            <Route path="motoboys-management" element={<MotoboysManagement />} />
            <Route path="banners" element={<BannersSection />} />
            <Route path="motoboy-deliveries" element={<MotoboyDeliveries />} />
            <Route path="product-options" element={<ProductOptionsManager />} />
            <Route path="mesas" element={<MesasManagement />} />
            <Route path="subscriptions" element={<Subscriptions />} />
            <Route path="promocoes" element={<Promocoes />} />
            <Route path="chat" element={<InternalChat />} />
            <Route path="customer-features" element={<CustomerFeatures />} />
            <Route path="android-update" element={<AndroidAppUpdate />} />
            <Route path="delivery-pricing" element={<DeliveryPricing />} />
            <Route path="stripe-test" element={<StripeTestLab />} />
            <Route path="whatsapp-test" element={<WhatsAppTestLab />} />
            <Route path="whatsapp-monitoring" element={<WhatsAppMonitoring />} />
            <Route path="operations" element={<OperationsCenter />} />
          </Routes>
        </Layout>
      </ProtectedRoute>
    </AuthProvider>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <UpdateAnnouncement />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/pedir" element={<CustomerApp />} />
          <Route path="/app/mesa/:token" element={<PublicMesaOrder />} />
          <Route path="/app/*" element={<CustomerApp />} />
          <Route path="/page" element={<Navigate to="/" replace />} />
          <Route path="/landing" element={<Navigate to="/" replace />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/criar-loja" element={<StoreSignup />} />
          <Route path="/onboarding" element={<AuthProvider><ProtectedRoute><StoreOnboarding /></ProtectedRoute></AuthProvider>} />

          <Route path="/admin/login" element={<AuthProvider><Login /></AuthProvider>} />
          <Route
            path="/admin/impressora"
            element={
              <AuthProvider>
                <ProtectedRoute>
                  <PrintStation />
                </ProtectedRoute>
              </AuthProvider>
            }
          />
          <Route path="/admin/motoboy/*" element={<AdminMotoboyRoutes prefix="/admin" />} />
          <Route path="/admin/*" element={<AdminRoutes prefix="/admin" />} />

          <Route path="/login" element={<Navigate to="/admin/login" replace />} />
          <Route path="/motoboy/*" element={<AdminMotoboyRoutes />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </Router>
    </ThemeProvider>
  );
}

export default App;
